/* eslint-disable ember/no-computed-properties-in-native-classes */
import Model, {
  attr,
  belongsTo,
  hasMany,
  type AsyncBelongsTo,
  type AsyncHasMany,
} from '@warp-drive/legacy/model';
import { computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { isEmpty } from '@ember/utils';
import type { Type } from '@warp-drive/core/types/symbols';
import PaymentMethod from '@linc-technologies/linced-common/enums/payment-method';
import { resolved } from '@linc-technologies/linced-common/utils/arrays';
import type DepositSlip from 'linced-app/models/finance/deposit-slip';
import type Discrepancy from 'linced-app/models/finance/discrepancy';
import type Till from 'linced-app/models/finance/till';
import type Person from 'linced-app/models/person';
import type School from 'linced-app/models/school';
import { sumDiscrepancies } from 'linced-app/utils/finance';

// Proto: finance.TillSession.
export default class TillSession extends Model {
  declare [Type]: 'finance/till-session';

  @belongsTo('school', { async: true, inverse: null })
  declare schoolId: AsyncBelongsTo<School>;

  @alias('schoolId')
  declare school: AsyncBelongsTo<School>;

  @attr('string', { defaultValue: '' })
  declare campusId: string; // TODO: implement when we have Campuses

  @attr('number')
  declare updateTime: number | undefined;

  @attr('number')
  declare createTime: number | undefined;

  // tillId specifies the Till which this Session is for.
  @belongsTo('finance/till', { async: true, inverse: null })
  declare tillId: AsyncBelongsTo<Till>;

  @alias('tillId')
  declare till: AsyncBelongsTo<Till>;

  // personId specifies the Person who opened this Till Session.
  @belongsTo('person', { async: true, inverse: null })
  declare personId: AsyncBelongsTo<Person>;

  @alias('personId')
  declare person: AsyncBelongsTo<Person>;

  // isOpen specifies whether this Till Session is currently open.
  @attr('boolean', { defaultValue: false })
  declare isOpen: boolean;

  // depositSlipId specifies the Deposit Slip which this Till Session
  // contributes its balance to, once created.
  @belongsTo('finance/deposit-slip', { async: true, inverse: null })
  declare depositSlipId: AsyncBelongsTo<DepositSlip>;

  @alias('depositSlipId')
  declare depositSlip: AsyncBelongsTo<DepositSlip>;

  // balance is the current balance of this Till Session, computed by the
  // backend service.
  @attr('number', { defaultValue: 0 })
  declare balance: number;

  // hundredsCount is the number of $100 bills.
  @attr('number', { defaultValue: 0 })
  declare hundredsCount: number;

  // hundredsAmount calculates the value of the $100 bills.
  @computed('hundredsCount')
  get hundredsAmount() {
    return this.hundredsCount * 100;
  }

  // fiftiesCount is the number of $50 bills.
  @attr('number', { defaultValue: 0 })
  declare fiftiesCount: number;

  // fiftiesAmount calculates the value of the $50 bills.
  @computed('fiftiesCount')
  get fiftiesAmount() {
    return this.fiftiesCount * 50;
  }

  // twentiesCount is the number of $20 bills.
  @attr('number', { defaultValue: 0 })
  declare twentiesCount: number;

  // twentiesAmount calculates the value of the $20 bills.
  @computed('twentiesCount')
  get twentiesAmount() {
    return this.twentiesCount * 20;
  }

  // tensCount is the number of $10 bills.
  @attr('number', { defaultValue: 0 })
  declare tensCount: number;

  // tensAmount calculates the value of the $10 bills.
  @computed('tensCount')
  get tensAmount() {
    return this.tensCount * 10;
  }

  // fivesCount is the number of $5 bills.
  @attr('number', { defaultValue: 0 })
  declare fivesCount: number;

  // fivesAmount calculates the value of the $5 bills.
  @computed('fivesCount')
  get fivesAmount() {
    return this.fivesCount * 5;
  }

  // coinsCount is the number of coins.
  @attr('number', { defaultValue: 0 })
  declare coinsCount: number;

  // coinsAmount is the value of the coins.
  @attr('number', { defaultValue: 0 })
  declare coinsAmount: number;

  // totalCount calculates the total number of bills and coins.
  @computed(
    'hundredsCount',
    'fiftiesCount',
    'twentiesCount',
    'tensCount',
    'fivesCount',
    'coinsCount',
  )
  get totalCount() {
    return (
      this.hundredsCount +
      this.fiftiesCount +
      this.twentiesCount +
      this.tensCount +
      this.fivesCount +
      this.coinsCount
    );
  }

  // totalAmount calculates the total value of cash to be banked.
  @computed(
    'hundredsAmount',
    'fiftiesAmount',
    'twentiesAmount',
    'tensAmount',
    'fivesAmount',
    'coinsAmount',
  )
  get totalAmount() {
    const notesAmount =
      this.hundredsAmount +
      this.fiftiesAmount +
      this.twentiesAmount +
      this.tensAmount +
      this.fivesAmount;
    if (isEmpty(this.coinsAmount)) {
      return notesAmount;
    }
    // @ts-expect-error coinsAmount is a number; parseFloat returns it as is.
    return notesAmount + parseFloat(this.coinsAmount);
  }

  // discrepancies provides a list of discrepancies associated with this
  // Till Session.
  @hasMany('finance/discrepancy', { async: true, inverse: 'tillSessionId' })
  declare discrepancies: AsyncHasMany<Discrepancy>;

  // cashDiscrepanciesTotal calculates the total of any cash discrepancies.
  @computed('discrepancies.content.@each.{method,amount}')
  get cashDiscrepanciesTotal() {
    return sumDiscrepancies(resolved(this.discrepancies), PaymentMethod.CASH);
  }
  // chequeDiscrepanciesTotal calculates the total of any cheque discrepancies.
  @computed('discrepancies.content.@each.{method,amount}')
  get chequeDiscrepanciesTotal() {
    return sumDiscrepancies(resolved(this.discrepancies), PaymentMethod.CHEQUE);
  }
  // discrepanciesTotal calculates the total of any discrepancies.
  @computed('discrepancies.content.@each.amount')
  get discrepanciesTotal() {
    return sumDiscrepancies(resolved(this.discrepancies));
  }
}
